import React, { useEffect, useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Input } from '@appkit4/react-components';
import { DocumentTypeFieldResponse } from '../../../api/generated/models';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from '../../../Constants';
import './DocTypeSettings.scss';

enum FieldTypes {
    Text = 1,
    Number = 2,
    Date = 3,
    Checkbox = 4,
}

// Sortable Row Component
const SortableTableRow = ({ field, index, children }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.fieldId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={`table-row ${isDragging ? 'dragging' : ''}`}
            {...attributes}
        >
            {/* Drag Handle Column */}
            <td className="drag-handle" {...listeners}>
                <div className="drag-icon">⋮⋮</div>
            </td>

            {/* Order Number Column */}
            <td className="order-number">{index + 1}</td>

            {children}
        </tr>
    );
};

// Main Table Component
const DraggableFieldsTable = ({
    fields,
    onFieldsUpdate,
    isValidationEnabled,
}: any) => {
    const [sortedFields, setSortedFields] = useState(fields);
    const [isChanged, setIsChanged] = useState(false);
    const [data, setData] = useState<DocumentTypeFieldResponse[]>([]);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const docType = searchParams.get('docType');
    const docTypeId = parseInt(searchParams.get('docTypeId') || '0', 10);
    const processTypeId = parseInt(
        searchParams.get('processTypeId') || '0',
        10
    );

    const fieldTypeToString = (fieldTypeId: FieldTypes | undefined): string => {
        switch (fieldTypeId) {
            case FieldTypes.Text:
                return 'Text';
            case FieldTypes.Number:
                return 'Number';
            case FieldTypes.Date:
                return 'Date';
            case FieldTypes.Checkbox:
                return 'Checkbox';
            default:
                return 'Unknown';
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (fields && fields.length > 0) {
            // Check if all orderNumbers are 0
            const allOrderNumbersZero = fields.every(
                (field: any) => field.orderNumber === 0
            );

            let initialSortedFields;

            if (allOrderNumbersZero) {
                // Sort by fieldId and assign orderNumbers from 1 to fields.length
                initialSortedFields = [...fields]
                    .sort((a, b) => a.fieldId - b.fieldId)
                    .map((field, index) => ({
                        ...field,
                        orderNumber: index + 1,
                    }));
            } else {
                // Sort by existing orderNumber
                initialSortedFields = [...fields].sort(
                    (a, b) => a.orderNumber - b.orderNumber
                );
            }

            setSortedFields(initialSortedFields);
        }
        if (data) {
            return;
        }
    }, [fields]);

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setSortedFields((items: any) => {
                const oldIndex = items.findIndex(
                    (item: any) => item.fieldId === active.id
                );
                const newIndex = items.findIndex(
                    (item: any) => item.fieldId === over.id
                );
                setIsChanged(true);
                const newOrder = arrayMove(items, oldIndex, newIndex);

                const updatedFields = newOrder.map((field: any, index) => ({
                    ...field,
                    orderNumber: index + 1,
                }));

                return updatedFields;
            });
        }
    };

    const handleFieldUpdate = (fieldId: any, updates: any) => {
        const updatedFields = sortedFields.map((field: any) =>
            field.fieldId === fieldId ? { ...field, ...updates } : field
        );

        setSortedFields(updatedFields);
        setIsChanged(false);

        // Call callback to parent if provided
        if (onFieldsUpdate) {
            onFieldsUpdate(updatedFields);
        }
    };

    return (
        <div className="document-settings">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <div className="ap-table extraction-table ap-table-condensed">
                    <table className="table-head-padding">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Order</th>
                                <th>Field name</th>
                                <th>Field type</th>
                                <th>Minimum confidence</th>
                                <th>Normalisation prompt</th>
                                <th style={{ width: '200px' }}>
                                    Validation settings
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            <SortableContext
                                items={sortedFields.map((f: any) => f.fieldId)}
                                strategy={verticalListSortingStrategy}
                            >
                                {sortedFields.map(
                                    (field: any, index: number) => (
                                        <SortableTableRow
                                            key={field.fieldId}
                                            field={field}
                                            index={index}
                                        >
                                            <td>
                                                <Input
                                                    style={{
                                                        maxWidth: '400px',
                                                    }}
                                                    type="text"
                                                    value={
                                                        field.fieldName || ''
                                                    }
                                                    onChange={(
                                                        value: string
                                                    ) => {
                                                        setIsChanged(true);
                                                        const newData = [
                                                            ...fields,
                                                        ];
                                                        const index =
                                                            newData.findIndex(
                                                                (d) =>
                                                                    d.fieldId ===
                                                                    field.fieldId
                                                            );
                                                        newData[
                                                            index
                                                        ].fieldName = value;
                                                        setData(newData);
                                                        setSortedFields(
                                                            (
                                                                prevFields: any[]
                                                            ) => {
                                                                return prevFields.map(
                                                                    (f: any) =>
                                                                        f.fieldId ===
                                                                        field.fieldId
                                                                            ? {
                                                                                  ...f,
                                                                                  fieldName:
                                                                                      value,
                                                                              }
                                                                            : f
                                                                );
                                                            }
                                                        );
                                                    }}
                                                    hideTitleOnInput
                                                />
                                            </td>
                                            <td>
                                                {fieldTypeToString(
                                                    field.fieldTypeId
                                                )}
                                            </td>
                                            <td>
                                                <div className="minimum-confidence-container">
                                                    <Input
                                                        style={{
                                                            maxWidth: '300px',
                                                        }}
                                                        type="number"
                                                        value={
                                                            field.minimumConfidence !==
                                                                undefined &&
                                                            field.minimumConfidence !==
                                                                null
                                                                ? Math.round(
                                                                      field.minimumConfidence *
                                                                          100
                                                                  ).toString()
                                                                : ''
                                                        }
                                                        onChange={(
                                                            value: string
                                                        ) => {
                                                            if (value === '') {
                                                                const newData =
                                                                    [...fields];
                                                                const index =
                                                                    newData.findIndex(
                                                                        (d) =>
                                                                            d.fieldId ===
                                                                            field.fieldId
                                                                    );
                                                                newData[
                                                                    index
                                                                ].minimumConfidence =
                                                                    null;
                                                                setData(
                                                                    newData
                                                                );
                                                                setIsChanged(
                                                                    true
                                                                );
                                                                setSortedFields(
                                                                    (
                                                                        prevFields: any[]
                                                                    ) => {
                                                                        return prevFields.map(
                                                                            (
                                                                                f: any
                                                                            ) =>
                                                                                f.fieldId ===
                                                                                field.fieldId
                                                                                    ? {
                                                                                          ...f,
                                                                                          minimumConfidence:
                                                                                              null,
                                                                                      }
                                                                                    : f
                                                                        );
                                                                    }
                                                                );
                                                                return;
                                                            }

                                                            const numValue =
                                                                parseFloat(
                                                                    value
                                                                );
                                                            if (isNaN(numValue))
                                                                return;

                                                            const clampedValue =
                                                                Math.min(
                                                                    Math.max(
                                                                        numValue,
                                                                        0
                                                                    ),
                                                                    100
                                                                );

                                                            const finalValue =
                                                                Math.round(
                                                                    clampedValue *
                                                                        10000
                                                                ) / 1000000;

                                                            const newData = [
                                                                ...fields,
                                                            ];
                                                            const index =
                                                                newData.findIndex(
                                                                    (d) =>
                                                                        d.fieldId ===
                                                                        field.fieldId
                                                                );
                                                            newData[
                                                                index
                                                            ].minimumConfidence =
                                                                finalValue;
                                                            setData(newData);
                                                            setIsChanged(true);
                                                            setSortedFields(
                                                                (
                                                                    prevFields: any[]
                                                                ) => {
                                                                    return prevFields.map(
                                                                        (
                                                                            f: any
                                                                        ) =>
                                                                            f.fieldId ===
                                                                            field.fieldId
                                                                                ? {
                                                                                      ...f,
                                                                                      minimumConfidence:
                                                                                          finalValue,
                                                                                  }
                                                                                : f
                                                                    );
                                                                }
                                                            );
                                                        }}
                                                        min={0}
                                                        max={100}
                                                        hideTitleOnInput
                                                    />
                                                    <span className="confidence-percentage">
                                                        %
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <Input
                                                    style={{
                                                        maxWidth: '350px',
                                                    }}
                                                    type="text"
                                                    value={
                                                        field.normalisationPrompt ||
                                                        ''
                                                    }
                                                    onChange={(
                                                        value: string
                                                    ) => {
                                                        setIsChanged(true);
                                                        const newData = [
                                                            ...fields,
                                                        ];
                                                        const index =
                                                            newData.findIndex(
                                                                (d) =>
                                                                    d.fieldId ===
                                                                    field.fieldId
                                                            );
                                                        newData[
                                                            index
                                                        ].normalisationPrompt =
                                                            value;
                                                        setData(newData);
                                                        setSortedFields(
                                                            (
                                                                prevFields: any[]
                                                            ) => {
                                                                return prevFields.map(
                                                                    (f: any) =>
                                                                        f.fieldId ===
                                                                        field.fieldId
                                                                            ? {
                                                                                  ...f,
                                                                                  normalisationPrompt:
                                                                                      value,
                                                                              }
                                                                            : f
                                                                );
                                                            }
                                                        );
                                                    }}
                                                    placeholder="Enter normalisation prompt"
                                                    hideTitleOnInput
                                                />
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        color: isValidationEnabled
                                                            ? '#474747'
                                                            : '#c0c0c0',
                                                        fontSize: '20px',
                                                        cursor: isValidationEnabled
                                                            ? 'pointer'
                                                            : 'not-allowed',
                                                        marginLeft: '60px',
                                                    }}
                                                    className="Appkit4-icon icon-setting-outline"
                                                    role="button"
                                                    aria-label="Settings"
                                                    onClick={() => {
                                                        if (
                                                            isValidationEnabled
                                                        ) {
                                                            navigate(
                                                                `${
                                                                    AppRoutes.ExtractionValidationSettings
                                                                }?docType=${docType}&docTypeId=${docTypeId}&fieldName=${
                                                                    field.fieldName
                                                                }&fieldType=${fieldTypeToString(
                                                                    field.fieldTypeId
                                                                )}&fieldId=${
                                                                    field.fieldId
                                                                }&processTypeId=${processTypeId}`
                                                            );
                                                        }
                                                    }}
                                                />
                                            </td>
                                        </SortableTableRow>
                                    )
                                )}
                            </SortableContext>
                        </tbody>
                    </table>
                </div>
                <div className="table-footer">
                    <Button
                        kind="secondary"
                        disabled={!isChanged}
                        onClick={handleFieldUpdate}
                    >
                        Save
                    </Button>
                </div>
            </DndContext>
        </div>
    );
};

export default React.memo(DraggableFieldsTable);
